import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, X } from 'lucide-react';
import { GameCard } from './SuggestionsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/upload-games`;
const pct = (p) => (p == null ? '—' : `${Math.round(p * 100)}%`);
const WHAT = { cfb: 'winner / loser', nfl: 'winner + passing, rushing, receiving and TD picks', nba: 'winner + points, rebounds, assists and 3s' };

const Verdict = ({ r, testId }) => {
  if (r.edge == null) return null;
  const e = Math.round(r.edge * 100);
  if (r.value) return <span className="rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-black text-black" data-testid={testId}>GOOD VALUE +{e}%</span>;
  if (e <= -8) return <span className="rounded bg-red-500/20 border border-red-500/50 px-1.5 py-0.5 text-[10px] font-bold text-red-300" data-testid={testId}>{r.book || 'App'} more confident than us</span>;
  return <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400" data-testid={testId}>Fair price ({e >= 0 ? '+' : ''}{e}%)</span>;
};

const Row = ({ r, league, i }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2 space-y-1" data-testid={`upg-game-${i}`}>
    <div className="flex flex-wrap items-center gap-2">
      <p className="text-sm font-bold text-white">{r.game.short_name || r.uploaded}</p>
      <Verdict r={r} testId={`upg-verdict-${i}`} />
      {r.when && <span className="text-[11px] text-zinc-500">{r.when}</span>}
    </div>
    {r.pick?.team ? (
      <p className="text-[13px] text-zinc-200" data-testid={`upg-pick-${i}`}>
        {r.pick.prob != null && r.pick.prob < 0.53 && <span className="mr-1 rounded bg-amber-500/20 px-1 text-[10px] font-bold text-amber-300">TOSS-UP</span>}
        Pick: <b className="text-emerald-300">{r.pick.team}</b> to win · our chance <b>{pct(r.pick.prob)}</b>
        {r.book_prob != null && <> · {r.book || 'app'} says <b>{pct(r.book_prob)}</b></>}
      </p>
    ) : <p className="text-[12px] text-zinc-500">No winner pick yet for this game.</p>}
    {r.pick?.reason && <p className="text-[11px] text-zinc-400">{r.pick.reason}</p>}
    {league !== 'cfb' && r.game.players?.length > 0 && <GameCard g={r.game} />}
  </div>
);

// Upload a Kalshi / FanDuel screenshot or screen recording of upcoming games → picks for just those games.
export default function UploadGames({ league }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const send = async (files) => {
    if (!files?.length) return;
    setBusy(true); setRes(null);
    try {
      const fd = new FormData();
      [...files].slice(0, 15).forEach((f) => fd.append('files', f));
      const { job_id: id } = (await axios.post(`${API}/${league}`, fd)).data;
      let misses = 0;
      for (let k = 0; k < 100; k++) {
        await new Promise((r) => setTimeout(r, 3000));
        let j;
        try { j = (await axios.get(`${API}/jobs/${id}`)).data; misses = 0; } catch (err) { if (++misses > 8) throw err; continue; }
        if (j.status === 'done') { setRes(j.result); toast.success(`Found ${j.result.games.length} game${j.result.games.length === 1 ? '' : 's'}`); break; }
        if (j.status === 'error') { toast.error(j.error); break; }
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
    if (input.current) input.current.value = '';
  };
  return (
    <section className="rounded-xl border border-violet-500/40 bg-violet-950/20 p-3 space-y-2" data-testid={`upload-games-${league}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-violet-200">Upload games from Kalshi or FanDuel</p>
          <p className="text-[11px] text-zinc-400">A screenshot or screen recording of one or many upcoming games → {WHAT[league]} for every game shown, compared with the app's odds.</p>
        </div>
        <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`upload-games-btn-${league}`}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-violet-500 px-3 py-2 text-xs font-bold text-white hover:bg-violet-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{busy ? 'Reading…' : 'Upload'}
        </button>
        <input ref={input} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => send(e.target.files)} data-testid={`upload-games-input-${league}`} />
      </div>
      {busy && <p className="text-[11px] text-violet-200">Reading your upload — videos take about a minute.</p>}
      {res && (
        <div className="space-y-2" data-testid={`upload-games-result-${league}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-white">{res.games.length} game{res.games.length === 1 ? '' : 's'} from your {res.book || 'upload'} · {res.games.filter((g) => g.value).length} good value</p>
            <button type="button" onClick={() => setRes(null)} className="text-zinc-500 hover:text-white" data-testid={`upload-games-clear-${league}`}><X className="w-4 h-4" /></button>
          </div>
          {res.games.map((r, i) => <Row key={r.game.event_id} r={r} league={league} i={i} />)}
          {res.not_found?.length > 0 && <p className="text-[11px] text-amber-300" data-testid="upg-not-found">Not on our board yet{league === 'nfl' ? " (NFL picks open per game day)" : ''}: {res.not_found.join(', ')}</p>}
          {res.errors?.length > 0 && <p className="text-[11px] text-red-400">{res.errors.join('; ')}</p>}
        </div>
      )}
    </section>
  );
}
